import { useState, useEffect } from "react";
import axios from "axios";
import { Calendar, Users, DollarSign, Clock, LogOut, Check, X, Trash2, RefreshCw, Settings, Plus, Edit2, Scissors, User, ChevronLeft, ChevronRight } from "lucide-react";
import "./Admin.css";

const API = '/api';

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/admin/login`, {
        email,
        password
      });

      if (response.data.success) {
        localStorage.setItem("adminToken", response.data.token);
        onLogin(response.data.token);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login" data-testid="admin-login">
      <div className="login-card">
        <div className="login-header">
          <h1>Le Grand Paris</h1>
          <p>Administration</p>
        </div>
        
        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              required
              data-testid="admin-email"
            />
          </div>
          
          <div className="form-group">
            <label>Mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              data-testid="admin-password"
            />
          </div>
          
          <button type="submit" className="login-btn" disabled={loading} data-testid="admin-submit">
            {loading ? "Connexion..." : "Se connecter"}
          </button>
        </form>
      </div>
    </div>
  );
};

// Settings Component
const SettingsPanel = ({ services, barbers, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('services');
  const [editingService, setEditingService] = useState(null);
  const [editingBarber, setEditingBarber] = useState(null);
  const [newService, setNewService] = useState({ name: '', description: '', price: '', duration: 30, category: 'homme' });
  const [newBarber, setNewBarber] = useState({ name: '' });
  const [showAddService, setShowAddService] = useState(false);
  const [showAddBarber, setShowAddBarber] = useState(false);
  const [appSettings, setAppSettings] = useState({});
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoadingSettings(true);
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setAppSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    setSaveStatus('Enregistrement...');
    try {
      await axios.post(`${API}/admin/settings`, appSettings);
      setSaveStatus('✅ Enregistré !');
      setTimeout(() => setSaveStatus(''), 3000);
      onUpdate(); // Trigger refresh if needed
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveStatus('❌ Erreur');
    }
  };

  const handleAddService = async () => {
    if (!newService.name || !newService.price) return;
    try {
      await axios.post(`${API}/services`, {
        ...newService,
        price: parseFloat(newService.price),
        duration: parseInt(newService.duration)
      });
      setNewService({ name: '', description: '', price: '', duration: 30, category: 'homme' });
      setShowAddService(false);
      onUpdate();
    } catch (error) {
      console.error('Error adding service:', error);
    }
  };

  const handleUpdateService = async (serviceId) => {
    if (!editingService) return;
    try {
      await axios.put(`${API}/services/${serviceId}`, {
        name: editingService.name,
        description: editingService.description,
        price: parseFloat(editingService.price),
        duration: parseInt(editingService.duration),
        category: editingService.category
      });
      setEditingService(null);
      onUpdate();
    } catch (error) {
      console.error('Error updating service:', error);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Supprimer ce service ?')) return;
    try {
      await axios.delete(`${API}/services/${serviceId}`);
      onUpdate();
    } catch (error) {
      console.error('Error deleting service:', error);
    }
  };

  const handleAddBarber = async () => {
    if (!newBarber.name) return;
    try {
      await axios.post(`${API}/barbers`, newBarber);
      setNewBarber({ name: '' });
      setShowAddBarber(false);
      onUpdate();
    } catch (error) {
      console.error('Error adding barber:', error);
    }
  };

  const handleUpdateBarber = async (barberId) => {
    if (!editingBarber) return;
    try {
      await axios.put(`${API}/barbers/${barberId}`, {
        name: editingBarber.name
      });
      setEditingBarber(null);
      onUpdate();
    } catch (error) {
      console.error('Error updating barber:', error);
    }
  };

  const handleDeleteBarber = async (barberId) => {
    if (!window.confirm('Supprimer ce coiffeur ?')) return;
    try {
      await axios.delete(`${API}/barbers/${barberId}`);
      onUpdate();
    } catch (error) {
      console.error('Error deleting barber:', error);
    }
  };

  const handleToggleBarberAvailability = async (barber) => {
    try {
      await axios.put(`${API}/barbers/${barber.id}`, {
        available: !barber.available
      });
      onUpdate();
    } catch (error) {
      console.error('Error toggling barber:', error);
    }
  };

  return (
    <div className="settings-panel">
      <div className="settings-tabs">
        <button 
          className={`settings-tab ${activeTab === 'services' ? 'active' : ''}`}
          onClick={() => setActiveTab('services')}
        >
          <Scissors size={18} />
          Services & Prix
        </button>
        <button 
          className={`settings-tab ${activeTab === 'barbers' ? 'active' : ''}`}
          onClick={() => setActiveTab('barbers')}
        >
          <User size={18} />
          Coiffeurs
        </button>
        <button 
          className={`settings-tab ${activeTab === 'config' ? 'active' : ''}`}
          onClick={() => setActiveTab('config')}
        >
          <Settings size={18} />
          Configuration
        </button>
      </div>

      {activeTab === 'services' && (
        <div className="settings-content">
          <div className="settings-header">
            <h3>Services & Tarifs</h3>
            <button className="add-btn" onClick={() => setShowAddService(!showAddService)}>
              <Plus size={18} />
              Ajouter
            </button>
          </div>

          {showAddService && (
            <div className="add-form">
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Nom du service"
                  value={newService.name}
                  onChange={(e) => setNewService({...newService, name: e.target.value})}
                />
                <input
                  type="number"
                  placeholder="Prix (€)"
                  value={newService.price}
                  onChange={(e) => setNewService({...newService, price: e.target.value})}
                />
              </div>
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Description"
                  value={newService.description}
                  onChange={(e) => setNewService({...newService, description: e.target.value})}
                />
                <select
                  value={newService.category}
                  onChange={(e) => setNewService({...newService, category: e.target.value})}
                >
                  <option value="homme">Homme</option>
                  <option value="femme">Femme</option>
                  <option value="barbe">Barbe</option>
                </select>
              </div>
              <div className="form-row">
                <input
                  type="number"
                  placeholder="Durée (min)"
                  value={newService.duration}
                  onChange={(e) => setNewService({...newService, duration: e.target.value})}
                />
                <button className="save-btn" onClick={handleAddService}>
                  <Check size={16} /> Ajouter
                </button>
              </div>
            </div>
          )}

          <div className="items-list">
            {['homme', 'barbe', 'femme'].map(category => (
              <div key={category} className="category-section">
                <h4 className="category-title">{category.charAt(0).toUpperCase() + category.slice(1)}</h4>
                {services.filter(s => s.category === category && s.active !== false).map(service => (
                  <div key={service.id} className="item-row">
                    {editingService?.id === service.id ? (
                      <>
                        <input
                          type="text"
                          value={editingService.name}
                          onChange={(e) => setEditingService({...editingService, name: e.target.value})}
                          className="edit-input"
                        />
                        <input
                          type="number"
                          value={editingService.price}
                          onChange={(e) => setEditingService({...editingService, price: e.target.value})}
                          className="edit-input price-input"
                        />
                        <button className="icon-btn save" onClick={() => handleUpdateService(service.id)}>
                          <Check size={16} />
                        </button>
                        <button className="icon-btn cancel" onClick={() => setEditingService(null)}>
                          <X size={16} />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="item-name">{service.name}</span>
                        <span className="item-price">{service.price}€</span>
                        <span className="item-duration">{service.duration}min</span>
                        <button className="icon-btn edit" onClick={() => setEditingService(service)}>
                          <Edit2 size={16} />
                        </button>
                        <button className="icon-btn delete" onClick={() => handleDeleteService(service.id)}>
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'barbers' && (
        <div className="settings-content">
          <div className="settings-header">
            <h3>Coiffeurs</h3>
            <button className="add-btn" onClick={() => setShowAddBarber(!showAddBarber)}>
              <Plus size={18} />
              Ajouter
            </button>
          </div>

          {showAddBarber && (
            <div className="add-form">
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Nom du coiffeur"
                  value={newBarber.name}
                  onChange={(e) => setNewBarber({...newBarber, name: e.target.value})}
                />
                <button className="save-btn" onClick={handleAddBarber}>
                  <Check size={16} /> Ajouter
                </button>
              </div>
            </div>
          )}

          <div className="items-list barbers-list">
            {barbers.map(barber => (
              <div key={barber.id} className={`item-row barber-row ${!barber.available ? 'inactive' : ''}`}>
                {editingBarber?.id === barber.id ? (
                  <>
                    <input
                      type="text"
                      value={editingBarber.name}
                      onChange={(e) => setEditingBarber({...editingBarber, name: e.target.value})}
                      className="edit-input"
                    />
                    <button className="icon-btn save" onClick={() => handleUpdateBarber(barber.id)}>
                      <Check size={16} />
                    </button>
                    <button className="icon-btn cancel" onClick={() => setEditingBarber(null)}>
                      <X size={16} />
                    </button>
                  </>
                ) : (
                  <>
                    <div className="barber-avatar">{barber.name.charAt(0)}</div>
                    <span className="item-name">{barber.name}</span>
                    <span className={`status-indicator ${barber.available ? 'active' : 'inactive'}`}>
                      {barber.available ? 'Actif' : 'Inactif'}
                    </span>
                    <button 
                      className={`toggle-btn ${barber.available ? 'on' : 'off'}`}
                      onClick={() => handleToggleBarberAvailability(barber)}
                    >
                      {barber.available ? 'Désactiver' : 'Activer'}
                    </button>
                    <button className="icon-btn edit" onClick={() => setEditingBarber(barber)}>
                      <Edit2 size={16} />
                    </button>
                    <button className="icon-btn delete" onClick={() => handleDeleteBarber(barber.id)}>
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'config' && (
        <div className="settings-content">
          <div className="settings-header">
            <h3>Configuration Système</h3>
            {saveStatus && <span className="save-status">{saveStatus}</span>}
          </div>

          <form onSubmit={handleUpdateSettings} className="config-form">
            <div className="config-section">
              <h4>📧 Gmail / Nodemailer</h4>
              <div className="form-group">
                <label>Email Gmail (Expéditeur)</label>
                <input
                  type="email"
                  value={appSettings.EMAIL_USER || ''}
                  onChange={(e) => setAppSettings({...appSettings, EMAIL_USER: e.target.value})}
                  placeholder="votre.email@gmail.com"
                />
              </div>
              <div className="form-group">
                <label>Code d'application Gmail (16 caractères)</label>
                <input
                  type="password"
                  value={appSettings.EMAIL_PASS || ''}
                  onChange={(e) => setAppSettings({...appSettings, EMAIL_PASS: e.target.value})}
                  placeholder="•••• •••• •••• ••••"
                />
                <small>Généré sur votre compte Google &gt; Sécurité &gt; Mots de passe d'applications</small>
              </div>
              <div className="form-group">
                <label>Email Admin (Contact affiché)</label>
                <input
                  type="email"
                  value={appSettings.ADMIN_EMAIL || ''}
                  onChange={(e) => setAppSettings({...appSettings, ADMIN_EMAIL: e.target.value})}
                />
              </div>
            </div>

            <div className="config-section">
              <h4>🏢 Informations Salon</h4>
              <div className="form-group">
                <label>Nom du Salon</label>
                <input
                  type="text"
                  value={appSettings.SALON_NAME || ''}
                  onChange={(e) => setAppSettings({...appSettings, SALON_NAME: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Téléphone</label>
                <input
                  type="text"
                  value={appSettings.SALON_PHONE || ''}
                  onChange={(e) => setAppSettings({...appSettings, SALON_PHONE: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Adresse complète</label>
                <input
                  type="text"
                  value={appSettings.SALON_ADDRESS || ''}
                  onChange={(e) => setAppSettings({...appSettings, SALON_ADDRESS: e.target.value})}
                />
              </div>
            </div>

            <button type="submit" className="save-config-btn">
              <Check size={20} />
              Enregistrer les modifications
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

const AdminDashboard = ({ onLogout }) => {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [activeView, setActiveView] = useState('bookings'); // 'bookings' or 'settings'

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 30 seconds
    const refreshInterval = setInterval(() => {
      fetchData();
      setLastRefresh(new Date());
    }, 30 * 1000); // 30 seconds
    
    return () => clearInterval(refreshInterval);
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, bookingsRes, servicesRes, barbersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/bookings`),
        axios.get(`${API}/services/all`),
        axios.get(`${API}/barbers/all`)
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setServices(servicesRes.data);
      setBarbers(barbersRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status?status=${status}`);
      fetchData();
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette réservation ?")) return;
    
    try {
      await axios.delete(`${API}/bookings/${bookingId}`);
      fetchData();
    } catch (error) {
      console.error("Error deleting booking:", error);
    }
  };
  
  const handlePrevDay = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 1);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const handleNextDay = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const filteredBookings = bookings.filter(b => b.date === selectedDate);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  };

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      <header className="admin-header">
        <div className="admin-logo">
          <span>LE GRAND</span>
          <span className="gold">PARIS</span>
          <span className="admin-badge">Admin</span>
        </div>
        <div className="header-right">
          <span className="last-refresh">
            Dernière MAJ: {lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            <span className="auto-refresh-indicator">• Auto 30s</span>
          </span>
          <button className="logout-btn" onClick={onLogout} data-testid="logout-btn">
            <LogOut size={18} />
            Déconnexion
          </button>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="admin-nav">
        <button 
          className={`nav-tab ${activeView === 'bookings' ? 'active' : ''}`}
          onClick={() => setActiveView('bookings')}
        >
          <Calendar size={18} />
          Réservations
        </button>
        <button 
          className={`nav-tab ${activeView === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveView('settings')}
        >
          <Settings size={18} />
          Paramètres
        </button>
      </div>

      <main className="admin-main">
        {activeView === 'bookings' && (
          <>
            {/* Stats Cards */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon blue">
                  <Calendar size={24} />
                </div>
                <div className="stat-info">
                  <span className="stat-value">{stats?.total_bookings || 0}</span>
                  <span className="stat-label">Total RDV</span>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon green">
                  <Users size={24} />
                </div>
                <div className="stat-info">
                  <span className="stat-value">{stats?.today_bookings || 0}</span>
                  <span className="stat-label">RDV Aujourd'hui</span>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon gold">
                  <Clock size={24} />
                </div>
                <div className="stat-info">
                  <span className="stat-value">{stats?.confirmed_bookings || 0}</span>
                  <span className="stat-label">Confirmés</span>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon purple">
                  <DollarSign size={24} />
                </div>
                <div className="stat-info">
                  <span className="stat-value">{stats?.total_revenue?.toFixed(2) || 0}€</span>
                  <span className="stat-label">Chiffre d'affaires</span>
                </div>
              </div>
            </div>

            {/* Bookings Section */}
            <div className="bookings-section">
              <div className="section-header">
                <h2>Réservations</h2>
                <div className="section-actions">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="date-filter"
                    data-testid="date-filter"
                  />
                  <button className="refresh-btn" onClick={fetchData}>
                    <RefreshCw size={18} />
                  </button>
                </div>
              </div>

              <div className="date-navigator">
                <button className="nav-arrow" onClick={handlePrevDay} title="Jour précédent">
                  <ChevronLeft size={24} />
                </button>
                <div className="date-title">
                  {formatDate(selectedDate)}
                  <span className="booking-count">({filteredBookings.length} RDV)</span>
                </div>
                <button className="nav-arrow" onClick={handleNextDay} title="Jour suivant">
                  <ChevronRight size={24} />
                </button>
              </div>

              {loading ? (
                <div className="loading">Chargement...</div>
              ) : filteredBookings.length === 0 ? (
                <div className="no-bookings">Aucune réservation pour cette date</div>
              ) : (
                <div className="bookings-table">
                  <div className="table-header">
                    <span>Heure</span>
                    <span>Client</span>
                    <span>Service</span>
                    <span>Coiffeur</span>
                    <span>Prix</span>
                    <span>Statut</span>
                    <span>Actions</span>
                  </div>
                  
                  {filteredBookings
                    .sort((a, b) => a.time.localeCompare(b.time))
                    .map((booking) => (
                      <div key={booking.id} className="table-row" data-testid={`booking-${booking.id}`}>
                        <span className="time-cell">{booking.time}</span>
                        <span className="client-cell">
                          <strong>{booking.customer_name}</strong>
                          <small>{booking.customer_phone}</small>
                        </span>
                        <span>{booking.service_name}</span>
                        <span>{booking.barber_name}</span>
                        <span className="price-cell">{booking.service_price}€</span>
                        <span className={`status-badge ${booking.status}`}>
                          {booking.status === "confirmed" && "Confirmé"}
                          {booking.status === "completed" && "Terminé"}
                          {booking.status === "cancelled" && "Annulé"}
                        </span>
                        <span className="actions-cell">
                          {booking.status === "confirmed" && (
                            <>
                              <button
                                className="action-btn success"
                                onClick={() => updateStatus(booking.id, "completed")}
                                title="Marquer comme terminé"
                              >
                                <Check size={16} />
                              </button>
                              <button
                                className="action-btn danger"
                                onClick={() => updateStatus(booking.id, "cancelled")}
                                title="Annuler"
                              >
                                <X size={16} />
                              </button>
                            </>
                          )}
                          <button
                            className="action-btn delete"
                            onClick={() => deleteBooking(booking.id)}
                            title="Supprimer"
                          >
                            <Trash2 size={16} />
                          </button>
                        </span>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* All Bookings */}
            <div className="bookings-section">
              <h2>Toutes les réservations récentes</h2>
              
              <div className="bookings-list">
                {bookings.slice(0, 20).map((booking) => (
                  <div key={booking.id} className="booking-card">
                    <div className="booking-header">
                      <span className="confirmation-num">{booking.confirmation_number}</span>
                      <div className="booking-actions-group">
                        <span className={`status-badge ${booking.status}`}>
                          {booking.status === "confirmed" && "Confirmé"}
                          {booking.status === "completed" && "Terminé"}
                          {booking.status === "cancelled" && "Annulé"}
                        </span>
                        <button 
                          className="action-btn delete"
                          onClick={() => deleteBooking(booking.id)}
                          title="Supprimer"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="booking-body">
                      <div className="booking-info">
                        <strong>{booking.customer_name}</strong>
                        <span>{booking.customer_email}</span>
                        <span>{booking.customer_phone}</span>
                      </div>
                      <div className="booking-details">
                        <span>{booking.service_name} - {booking.service_price}€</span>
                        <span>Coiffeur: {booking.barber_name}</span>
                        <span>{booking.date} à {booking.time}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {activeView === 'settings' && (
          <SettingsPanel 
            services={services} 
            barbers={barbers} 
            onUpdate={fetchData} 
          />
        )}
      </main>
    </div>
  );
};

const Admin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      setIsLoggedIn(true);
    }
  }, []);

  const handleLogin = (token) => {
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    setIsLoggedIn(false);
  };

  return isLoggedIn ? (
    <AdminDashboard onLogout={handleLogout} />
  ) : (
    <AdminLogin onLogin={handleLogin} />
  );
};

export default Admin;
